import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import SearchInput from '../../../components/common/SearchInput';
import DeliveryBoyTable from '../components/DeliveryBoyTable';
import { FiPlus, FiDownload, FiFilter, FiRefreshCw, FiX, FiCopy, FiKey } from 'react-icons/fi';
import {
  DeliveryBoy,
  DeliveryBoyStatus,
  VehicleType,
  ShiftType,
  DeliveryArea,
  DeliveryBoyFilters
} from '../types';
import {
  getDeliveryBoys,
  deleteDeliveryBoy as apiDeleteDeliveryBoy,
  toggleDeliveryBoyStatus,
  resetDeliveryBoyPassword,
} from '../api';

const DeliveryBoyList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deliveryBoys, setDeliveryBoys] = useState<DeliveryBoy[]>([]);
  const [filteredDeliveryBoys, setFilteredDeliveryBoys] = useState<DeliveryBoy[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<DeliveryBoyFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [loginDetailsBoy, setLoginDetailsBoy] = useState<DeliveryBoy | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getDeliveryBoys();
      setDeliveryBoys(list);
    } catch (e) {
      showToast('Failed to load delivery boys', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Filter and search logic
  useEffect(() => {
    let filtered = [...deliveryBoys];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(boy =>
        boy.employeeId.toLowerCase().includes(query) ||
        boy.firstName.toLowerCase().includes(query) ||
        boy.lastName.toLowerCase().includes(query) ||
        boy.email.toLowerCase().includes(query) ||
        boy.phone.includes(query)
      );
    }

    // Apply other filters
    if (filters.status) {
      filtered = filtered.filter(boy => boy.status === filters.status);
    }

    if (filters.vehicleType) {
      filtered = filtered.filter(boy => boy.vehicleType === filters.vehicleType);
    }

    if (filters.shiftType) {
      filtered = filtered.filter(boy => boy.shiftType === filters.shiftType);
    }

    if (filters.deliveryArea) {
      filtered = filtered.filter(boy => boy.deliveryArea === filters.deliveryArea);
    }

    if (filters.isAvailable !== undefined) {
      filtered = filtered.filter(boy => boy.isAvailable === filters.isAvailable);
    }

    setFilteredDeliveryBoys(filtered);
  }, [deliveryBoys, searchQuery, filters]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (key: keyof DeliveryBoyFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const refreshData = async () => {
    await loadList();
  };

  // "View" opens the Login Details modal — that's what admin actually
  // needs for a delivery boy: phone (= login username) and a way to
  // reset the app password. Earlier this was an alert() stub.
  const handleView = (deliveryBoy: DeliveryBoy) => {
    setLoginDetailsBoy(deliveryBoy);
  };

  const handleEdit = (deliveryBoy: DeliveryBoy) => {
    navigate(`/admin/delivery-boys/edit/${deliveryBoy.id}`);
  };

  const handleDelete = async (deliveryBoy: DeliveryBoy) => {
    if (!window.confirm(`Delete ${deliveryBoy.firstName} ${deliveryBoy.lastName}? This soft-deletes — record can be restored from Trash.`)) {
      return;
    }
    try {
      await apiDeleteDeliveryBoy(deliveryBoy.id);
      setDeliveryBoys(prev => prev.filter(boy => boy.id !== deliveryBoy.id));
      showToast('Delivery boy deleted', 'success');
    } catch (error) {
      showToast('Failed to delete delivery boy', 'error');
    }
  };

  const handleToggleStatus = async (deliveryBoy: DeliveryBoy) => {
    try {
      const updated = await toggleDeliveryBoyStatus(deliveryBoy.id);
      setDeliveryBoys(prev => prev.map(boy => (boy.id === updated.id ? updated : boy)));
      showToast(
        `Delivery boy ${updated.status === DeliveryBoyStatus.ACTIVE ? 'activated' : 'deactivated'}`,
        'success',
      );
    } catch (error) {
      showToast('Failed to update status', 'error');
    }
  };

  // No backend endpoint for availability yet; keep local-only so the
  // UI still feels responsive. Wire up via PUT /:id when that route
  // lands — see delivery-boy.routes.ts.
  const handleToggleAvailability = async (deliveryBoy: DeliveryBoy) => {
    const newAvailability = !deliveryBoy.isAvailable;
    setDeliveryBoys(prev =>
      prev.map(boy => (boy.id === deliveryBoy.id ? { ...boy, isAvailable: newAvailability } : boy)),
    );
    showToast(`Marked as ${newAvailability ? 'available' : 'unavailable'} (local only)`, 'success');
  };

  const exportData = () => {
    const rows = filteredDeliveryBoys;
    if (rows.length === 0) {
      showToast('Nothing to export', 'error');
      return;
    }

    const escape = (v: any) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = [
      'Employee ID',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Status',
      'Vehicle Type',
      'Shift',
      'Area',
      'Available',
      'Created At',
    ];
    const lines = [headers.join(',')];
    for (const b of rows) {
      lines.push([
        escape(b.employeeId),
        escape(b.firstName),
        escape(b.lastName),
        escape(b.email),
        escape(b.phone),
        escape(b.status),
        escape(b.vehicleType),
        escape(b.shiftType),
        escape(b.deliveryArea),
        escape(b.isAvailable ? 'yes' : 'no'),
        escape((b as any).createdAt ?? ''),
      ].join(','));
    }

    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `delivery-boys-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast(`Exported ${rows.length} delivery ${rows.length === 1 ? 'boy' : 'boys'}`, 'success');
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== undefined && value !== '').length +
           (searchQuery.trim() ? 1 : 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{t('sidebar.deliveryBoys')}</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage your delivery team members and their assignments
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={exportData}
            className="flex items-center space-x-2"
          >
            <FiDownload className="w-4 h-4" />
            <span>{t('action.export')}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>{t('action.refresh')}</span>
          </Button>
          <Button
            onClick={() => navigate('/admin/delivery-boys/add')}
            className="flex items-center space-x-2"
          >
            <FiPlus className="w-4 h-4" />
            <span>{t('action.add')}</span>
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex-1 max-w-md">
            <SearchInput
              placeholder={t('common.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2"
            >
              <FiFilter className="w-4 h-4" />
              <span>{t('action.filter')}</span>
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </Button>
            
            {getActiveFiltersCount() > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Statuses</option>
                  {Object.values(DeliveryBoyStatus).map(status => (
                    <option key={status} value={status}>
                      {status.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Vehicle Type</label>
                <select
                  value={filters.vehicleType || ''}
                  onChange={(e) => handleFilterChange('vehicleType', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Vehicles</option>
                  {Object.values(VehicleType).map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Shift Type</label>
                <select
                  value={filters.shiftType || ''}
                  onChange={(e) => handleFilterChange('shiftType', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Shifts</option>
                  {Object.values(ShiftType).map(shift => (
                    <option key={shift} value={shift}>
                      {shift.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Delivery Area</label>
                <select
                  value={filters.deliveryArea || ''}
                  onChange={(e) => handleFilterChange('deliveryArea', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Areas</option>
                  {Object.values(DeliveryArea).map(area => (
                    <option key={area} value={area}>
                      {area.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Availability</label>
                <select
                  value={filters.isAvailable !== undefined ? filters.isAvailable.toString() : ''}
                  onChange={(e) => handleFilterChange('isAvailable', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All</option>
                  <option value="true">Available</option>
                  <option value="false">Unavailable</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Results Summary */}
      <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-300">
        <span>
          Showing {filteredDeliveryBoys.length} of {deliveryBoys.length} delivery boys
        </span>
        <span>
          Active: {deliveryBoys.filter(boy => boy.status === DeliveryBoyStatus.ACTIVE).length} |
          Available: {deliveryBoys.filter(boy => boy.isAvailable).length}
        </span>
      </div>

      {/* Table */}
      <DeliveryBoyTable
        deliveryBoys={filteredDeliveryBoys}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        onToggleAvailability={handleToggleAvailability}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded-md shadow-lg z-50 ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Login Details modal — what admin actually wants from "View".
          Shows the phone (= app login username) plus a reset-password
          control. Backend never echoes plaintext passwords back, so
          admin either uses "default = phone" or sets a custom one and
          shares it manually. */}
      {loginDetailsBoy && (
        <LoginDetailsModal
          deliveryBoy={loginDetailsBoy}
          onClose={() => setLoginDetailsBoy(null)}
          onPasswordReset={(msg) => showToast(msg, 'success')}
          onPasswordResetError={(msg) => showToast(msg, 'error')}
        />
      )}
    </div>
  );
};

interface LoginDetailsModalProps {
  deliveryBoy: DeliveryBoy;
  onClose: () => void;
  onPasswordReset: (message: string) => void;
  onPasswordResetError: (message: string) => void;
}

const LoginDetailsModal: React.FC<LoginDetailsModalProps> = ({
  deliveryBoy,
  onClose,
  onPasswordReset,
  onPasswordResetError,
}) => {
  const [customPassword, setCustomPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [lastSetPassword, setLastSetPassword] = useState<string | null>(null);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onPasswordReset(`${label} copied`);
    } catch {
      onPasswordResetError('Copy failed');
    }
  };

  const handleReset = async () => {
    setResetting(true);
    const newPwd = customPassword.trim() || deliveryBoy.phone;
    const res = await resetDeliveryBoyPassword(deliveryBoy.id, customPassword.trim() || undefined);
    setResetting(false);
    if (res.success) {
      setLastSetPassword(newPwd);
      onPasswordReset(`Password reset — share with ${deliveryBoy.firstName}`);
    } else {
      onPasswordResetError(res.message || 'Reset failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md ring-1 ring-slate-200 dark:ring-slate-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <FiKey className="text-emerald-600" />
              Login Details
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {deliveryBoy.firstName} {deliveryBoy.lastName} · {deliveryBoy.employeeId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
            aria-label="Close"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Phone (= login username) */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
              Login Phone (Username)
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded font-mono text-sm text-slate-800 dark:text-slate-100">
                {deliveryBoy.phone}
              </code>
              <button
                onClick={() => copy(deliveryBoy.phone, 'Phone')}
                className="px-3 py-2 rounded-md text-sm bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200"
                title="Copy"
              >
                <FiCopy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
              Delivery boy uses this number to log in to the delivery app.
            </p>
          </div>

          {/* Default password info */}
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300">
            <strong>Default password:</strong> the phone number itself, until reset. If the boy never logged in, share the phone as both username and password.
          </div>

          {/* Last reset preview */}
          {lastSetPassword && (
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-0.5">
                    New password — share now (won't be shown again)
                  </div>
                  <code className="font-mono text-sm text-emerald-900 dark:text-emerald-200">
                    {lastSetPassword}
                  </code>
                </div>
                <button
                  onClick={() => copy(lastSetPassword, 'Password')}
                  className="px-2 py-1 rounded text-xs bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                >
                  <FiCopy className="w-3 h-3" /> Copy
                </button>
              </div>
            </div>
          )}

          {/* Reset section */}
          <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
              Reset Password
            </label>
            <input
              type="text"
              value={customPassword}
              onChange={(e) => setCustomPassword(e.target.value)}
              placeholder="Leave blank to reset to phone number"
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-md"
            />
            <Button
              onClick={handleReset}
              disabled={resetting}
              className="mt-2 w-full"
            >
              {resetting ? 'Resetting…' : customPassword.trim() ? 'Set Custom Password' : 'Reset to Phone'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryBoyList;